import { useState, useCallback } from 'react'

interface CNPJData {
  cnpj: string
  razao_social: string
  nome_fantasia?: string
  situacao_cadastral: string
  data_inicio_atividade?: string
  logradouro?: string
  numero?: string
  bairro?: string
  cep?: string
  uf?: string
  municipio?: string
  email?: string
  telefones?: Array<{
    ddd: string
    numero: string
    is_fax: boolean
  }>
  capital_social?: string
}

interface CNPJLookupResult {
  success: boolean
  data?: CNPJData
  error?: string
}

export const useCNPJLookup = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Cache para evitar requests repetidos na mesma sessão
  const cache = new Map<string, CNPJLookupResult>()

  const formatCNPJ = (cnpj: string): string => {
    // Remove tudo que não é número
    const numbers = cnpj.replace(/\D/g, '')
    
    // Aplica máscara XX.XXX.XXX/XXXX-XX
    if (numbers.length === 14) {
      return numbers.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    }
    
    return numbers
  }

  const validateCNPJ = (cnpj: string): boolean => {
    const numbers = cnpj.replace(/\D/g, '')
    
    // Deve ter 14 dígitos
    if (numbers.length !== 14) return false
    
    // Não pode ser sequência de números iguais
    if (/^(\d)\1+$/.test(numbers)) return false
    
    // Validação do dígito verificador
    let sum = 0
    let weight = 2
    
    // Primeiro dígito
    for (let i = 11; i >= 0; i--) {
      sum += parseInt(numbers[i]) * weight
      weight = weight === 9 ? 2 : weight + 1
    }
    
    const firstDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    if (parseInt(numbers[12]) !== firstDigit) return false
    
    // Segundo dígito
    sum = 0
    weight = 2
    
    for (let i = 12; i >= 0; i--) {
      sum += parseInt(numbers[i]) * weight
      weight = weight === 9 ? 2 : weight + 1
    }
    
    const secondDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    return parseInt(numbers[13]) === secondDigit
  }

  const lookupCNPJ = useCallback(async (cnpj: string): Promise<CNPJLookupResult> => {
    const cleanCNPJ = cnpj.replace(/\D/g, '')
    
    // Validação básica
    if (!validateCNPJ(cleanCNPJ)) {
      const result = { success: false, error: 'CNPJ inválido' }
      setError(result.error)
      return result
    }

    // Verificar cache
    if (cache.has(cleanCNPJ)) {
      const cached = cache.get(cleanCNPJ)!
      setError(cached.error || null)
      return cached
    }

    setLoading(true)
    setError(null)

    try {
      console.log(`🔍 Buscando dados do CNPJ: ${cleanCNPJ}`)
      
      const response = await fetch(`https://api.opencnpj.org/${cleanCNPJ}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      })

      if (response.status === 404) {
        const result = { success: false, error: 'CNPJ não encontrado na base de dados' }
        cache.set(cleanCNPJ, result)
        setError(result.error)
        return result
      }

      if (response.status === 429) {
        const result = { success: false, error: 'Muitas consultas. Aguarde alguns segundos e tente novamente.' }
        setError(result.error)
        return result
      }

      if (!response.ok) {
        throw new Error(`Erro na consulta: ${response.status}`)
      }

      const data: CNPJData = await response.json()
      
      console.log('✅ Dados do CNPJ encontrados:', data.razao_social)
      
      const result = { success: true, data }
      cache.set(cleanCNPJ, result)
      
      return result

    } catch (error) {
      console.error('❌ Erro ao consultar CNPJ:', error)
      
      const result = { 
        success: false, 
        error: 'Erro ao consultar CNPJ. Verifique sua conexão e tente novamente.' 
      }
      
      setError(result.error)
      return result
      
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    lookupCNPJ,
    loading,
    error,
    clearError,
    formatCNPJ,
    validateCNPJ
  }
}
